import { Client } from '@microsoft/microsoft-graph-client';
import { OpportunityItem, UpdateTechnicalReviewPayload } from '../models/opportunity';
import { NovaOportunidadeForm } from '../components/NovaOportunidadeModal';

export class GraphService {
  private client: Client;
  private siteId: string;
  private listId: string;
  private powerAutomateUrl: string;

  constructor(client: Client, siteId: string, listId: string, powerAutomateUrl?: string) {
    this.client = client;
    this.siteId = siteId;
    this.listId = listId;
    this.powerAutomateUrl = powerAutomateUrl || '';
  }

  async getOpportunities(): Promise<OpportunityItem[]> {
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields')
      .get();

    return response.value.map((item: any) => {
      const f = item.fields;
      return {
        id: item.id,
        codigoOportunidade: f.Title || '',
        cliente: f.Cliente || '',
        contactoCliente: f.ContactoCliente || '',
        comercialEmail: f.ComercialEmail || '',
        comercialNome: f.ComercialNome || 'Comercial',
        statusOportunidade: f.StatusOportunidade || '1. Levantamento Inicial',
        ambitoNecessidades: f.AmbitoNecessidades || '',
        responsavelTecnicoEmail: f.TecnicoEmail || '',
        responsavelTecnicoNome: f.TecnicoNome || 'Técnico Não Atribuído',
        statusTecnico: f.StatusTecnico || 'Não Iniciado',
        parecerTecnico: f.ParecerTecnico || '',
        historicoParecer: f.HistoricoParecer || '',
        dataLimiteParecer: f.DataLimiteParecer ? f.DataLimiteParecer.substring(0, 10) : undefined,
        dataAceitacao: f.DataAceitacao ? f.DataAceitacao.substring(0, 10) : undefined,
      } as OpportunityItem;
    });
  }

  async getNextOpportunityCode(): Promise<string> {
    const year = new Date().getFullYear();
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields($select=Title)')
      .get();

    const items: any[] = response.value || [];
    const prefix = `OPP-${year}-`;
    let max = 0;
    items.forEach((item: any) => {
      const title: string = item.fields?.Title || '';
      if (title.startsWith(prefix)) {
        const num = parseInt(title.replace(prefix, ''), 10);
        if (!isNaN(num) && num > max) max = num;
      }
    });
    const next = String(max + 1).padStart(3, '0');
    return `${prefix}${next}`;
  }

  async getTeamMembers(): Promise<{ nome: string; email: string }[]> {
    const TEAM_ID = '050ad99b-0592-40db-92a9-6cd66b0643ea';
    const response = await this.client
      .api(`/teams/${TEAM_ID}/members`)
      .get();
    return (response.value || [])
      .map((m: any) => ({
        nome: m.displayName || '',
        email: m.email || m.mail || '',
      }))
      .filter((m: any) => m.email)
      .sort((a: any, b: any) => a.nome.localeCompare(b.nome));
  }

  async createOpportunity(form: NovaOportunidadeForm): Promise<void> {
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .post({
        fields: {
          Title: form.titulo,
          Cliente: form.cliente,
          ContactoCliente: form.contactoCliente,
          ComercialEmail: form.comercialEmail,
          ComercialNome: form.comercialNome,
          StatusOportunidade: form.statusOportunidade,
          AmbitoNecessidades: form.ambitoNecessidades,
          TecnicoEmail: form.tecnicoEmail,
          TecnicoNome: form.tecnicoNome,
          StatusTecnico: form.statusTecnico,
          DataLimiteParecer: form.dataLimiteParecer,
        },
      });
  }

  async updateOpportunityStatus(itemId: string, newStatus: string): Promise<void> {
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({ StatusOportunidade: newStatus });
  }

  async updateTechnicalReview(
    itemId: string,
    payload: UpdateTechnicalReviewPayload,
    tecnicoNome: string,
    historicoActual: string
  ): Promise<void> {
    const now = new Date();
    const dataHora = `${now.toLocaleDateString('pt-PT')} ${now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
    const novaEntrada = `[${dataHora}] ${tecnicoNome}:\n${payload.parecerTecnico}`;
    const novoHistorico = historicoActual
      ? `${novaEntrada}\n\n---\n\n${historicoActual}`
      : novaEntrada;

    const fieldsToUpdate: Record<string, any> = {
      StatusTecnico: payload.statusTecnico,
      ParecerTecnico: payload.parecerTecnico,
      HistoricoParecer: novoHistorico,
    };
    if (payload.novoStatusOportunidade) {
      fieldsToUpdate.StatusOportunidade = payload.novoStatusOportunidade;
    }
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update(fieldsToUpdate);
  }
}
